import { BackArrowIcon, BoldText, ConsequenceText, Container, Content, Description, Title, Wrapper } from './styles';
import React, { useMemo } from 'react';

import { CancelButtonBlock } from '../receipt/components/cancel-button';
import { TransactionDetails } from 'store/transactions/types';
import { useCancelBooking } from './use-cancel-booking.hook';
import { useIntl } from 'react-intl';
import useIsBookingCancelable from 'store/bookings/hooks/use-is-booking-cancelable';
import { useLocale } from 'hooks/use-locale.hook';
import { getCancellationConsequenceText } from 'utils/getCancellationPolicyText';

interface CancelBookingProps {
  setIsCancelBookingStep?: React.Dispatch<React.SetStateAction<boolean>>;
  transaction?: TransactionDetails;
}

export const CancelBooking = ({ setIsCancelBookingStep, transaction }: CancelBookingProps): JSX.Element => {
  const intl = useIntl();
  const locale = useLocale();

  const {
    onBack,
    onCancelButtonClick,
    formattedDate,
    vendorName,
    isMobileDevice,
    showMiniappPaymentsNavigation,
    patchTransactionData,
  } = useCancelBooking({ setIsCancelBookingStep, transaction });
  const showBackButton = useMemo(
    () => !(showMiniappPaymentsNavigation && isMobileDevice),
    [showMiniappPaymentsNavigation, isMobileDevice],
  );
  const { cancelable, notCancelableReason } = useIsBookingCancelable(transaction);
  const cancellationConsequenceText = useMemo(() => {
    if (!transaction?.details) return null;
    return getCancellationConsequenceText(
      {
        startAt: transaction.details.start_at,
        allowCancelDuring: transaction.details.allow_cancel_during,
        allowLateCancel: transaction.details.allow_late_cancel,
        cancelDuringFeeAmount: transaction.details.cancel_during_fee_amount,
        cancelDuringFeePercent: transaction.details.cancel_during_fee_percent,
        lateCancelFeeAmount: transaction.details.late_cancel_fee_amount,
        lateCancelFeePercent: transaction.details.late_cancel_fee_percent,
        cancellationWindowEndMinutes: transaction.details.cancellation_window_end_minutes,
        cancellationDescription: transaction.details.cancellation_description,
        currencyType: transaction.currency_type,
        locale,
      },
      intl,
      transaction.total_price > 0,
    );
  }, [transaction?.details, transaction?.currency_type, locale, intl]);
  return (
    <Container
      className="rb-rcpt-cancel"
      wrapping={showMiniappPaymentsNavigation && isMobileDevice ? 'externalModal' : 'internalModal'}
    >
      <Wrapper>
        <Content>
          {showBackButton && <BackArrowIcon onClick={onBack} data-testid="modal-wrapper-back-button" />}
          <Title>{intl.formatMessage({ id: 'cancel_booking?' })}</Title>
          <Description>
            {intl.formatMessage(
              { id: 'you_are_about_cancel_booking' },
              {
                vendorName: <BoldText>{vendorName}</BoldText>,
                bookingTime: formattedDate ? (
                  <BoldText>
                    {`${formattedDate.eventWeekDay}, ${formattedDate.eventDateMonthLong} ${
                      formattedDate.eventDateDay
                    }, ${intl.formatMessage({
                      id: 'at',
                    })} ${formattedDate.formatEventStartTime}`}
                  </BoldText>
                ) : null,
              },
            )}
            {cancellationConsequenceText ? <ConsequenceText>{cancellationConsequenceText}</ConsequenceText> : null}
          </Description>
        </Content>
        <CancelButtonBlock
          patchTransactionData={patchTransactionData}
          onCancel={onCancelButtonClick}
          cancelable={cancelable}
          notCancelableReason={notCancelableReason}
          isSwipeModalContent={showMiniappPaymentsNavigation}
        />
      </Wrapper>
    </Container>
  );
};
